import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowDownward as DownloadIcon,
  ArrowUpward as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import {
  getFileSyncStatusColorString,
  getFileSyncStatusText,
  getFileSyncStatusDescription,
} from '@/common/utils/statusUtils';
import { FileSyncStatus } from '@/common/models/enums';

interface StatusBadgeProps {
  status: FileSyncStatus | string;
  showDescription?: boolean;
  variant?: 'chip' | 'box' | 'inline';
  size?: 'small' | 'medium';
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case FileSyncStatus.UP_TO_DATE:
      return <CheckIcon fontSize="small" />;
    case FileSyncStatus.BEHIND:
      return <DownloadIcon fontSize="small" />;
    case FileSyncStatus.NEWER:
      return <UploadIcon fontSize="small" />;
    case FileSyncStatus.DELETED:
      return <DeleteIcon fontSize="small" />;
    case FileSyncStatus.NEW:
      return <AddIcon fontSize="small" />;
    default:
      return <HelpIcon fontSize="small" />;
  }
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  showDescription = false,
  variant = 'box',
  size = 'medium',
}) => {
  const statusColor = getFileSyncStatusColorString(status);
  const statusText = getFileSyncStatusText(status);
  const statusDescription = getFileSyncStatusDescription(status);
  const statusIcon = getStatusIcon(status);

  if (variant === 'chip') {
    return (
      <Chip
        icon={statusIcon}
        label={statusText}
        size={size}
        sx={{
          color: statusColor,
          borderColor: statusColor,
          '& .MuiChip-icon': {
            color: statusColor,
          },
        }}
        variant="outlined"
      />
    );
  }

  if (variant === 'inline') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {statusIcon}
        <Typography variant="body2" sx={{ color: statusColor }}>
          {statusText}
        </Typography>
      </Box>
    );
  }

  // Default 'box' variant
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: statusColor,
          minWidth: 65,
          mb: showDescription ? 0.5 : 0,
        }}
      >
        {statusIcon}
        <Typography
          variant="body2"
          sx={{
            ml: 1,
            fontWeight: 500,
            textTransform:
              status === FileSyncStatus.UP_TO_DATE ? 'none' : 'capitalize',
          }}
        >
          {statusText}
        </Typography>
      </Box>
      {showDescription && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            flex: 1,
            lineHeight: 1.3,
          }}
        >
          {statusDescription}
        </Typography>
      )}
    </Box>
  );
};
